import Link from "next/link";
import { FooterWhatsApp } from "./FooterWhatsApp";
import { FooterHomeLink } from "./FooterHomeLink";
import { ArrowRight, Mail, MapPin, Phone } from "lucide-react";
import { footerNav, site } from "@/lib/site";
import { Container } from "@/components/ui/Container";
import { Logo } from "@/components/ui/Logo";
import {
  LinkedinIcon,
  InstagramIcon,
  FacebookIcon,
  // YoutubeIcon,
  // XIcon,
  WhatsappIcon,
} from "@/components/ui/social-icons";

const socials = [
  { href: site.socials.linkedin, icon: LinkedinIcon, label: "LinkedIn" },
  { href: site.socials.instagram, icon: InstagramIcon, label: "Instagram" },
  { href: site.socials.facebook, icon: FacebookIcon, label: "Facebook" },
  // { href: site.socials.youtube, icon: YoutubeIcon, label: "YouTube" },
  // { href: site.socials.x, icon: XIcon, label: "X" },
  { href: site.socials.whatsapp, icon: WhatsappIcon, label: "WhatsApp" },
];

const contact = [
  { icon: Mail, value: site.email, href: `mailto:${site.email}` },
  { icon: Phone, value: site.phone, href: undefined },
  { icon: MapPin, value: `${site.address.line1}, ${site.address.line2}`, href: undefined },
];

/** Clean cream footer used only on /home3 - same background as the header, minimal content. */
/** `courseLinks` comes from the courses API (3 courses +"View all courses"), so the list follows the dashboard. */
export function FooterCream({ courseLinks }: { courseLinks: { label: string; href: string }[] }) {

  return (
    <footer className="mt-auto border-t border-line bg-cream text-ink-soft">
      <Container className="pt-14 sm:pt-16">
        <div className="grid gap-10 pb-12 sm:grid-cols-2 lg:grid-cols-[1.4fr_0.8fr_1.2fr_1.3fr] lg:gap-12">
          {/* brand */}
          <div className="space-y-5">
            <Logo />
            <p className="max-w-xs text-sm leading-relaxed">
              Empowering learners with practical skills, expert mentorship and career-focused education.
            </p>
            <div className="flex flex-wrap gap-2">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-line bg-white text-ink-soft transition-all hover:-translate-y-0.5 hover:border-brand-600 hover:bg-brand-600 hover:text-white"
                >
                  <s.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          <Col title="Company" links={footerNav.company} />
          <Col title="Courses" links={courseLinks} />

          {/* contact - spans the full row on tablets so the WhatsApp button sits at the far end of the screen */}
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="relative flex items-center justify-between gap-3">
              <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-gold-600">Get in touch</h3>
              {/* out of the flow (margin, not a transform, so the floating fixed button keeps working) so the 44px button never makes this heading sit lower than the other columns' */}
              <div className="absolute right-0 top-1/2 -mt-[22px]">
                <FooterWhatsApp />
              </div>
            </div>
            <ul className="mt-5 space-y-3.5 text-sm">
              {contact.map(({ icon: Icon, value, href }) => {
                const body = (
                  <>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-brand-600 shadow-soft">
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className={`min-w-0 break-words pt-1.5 font-normal text-black${href ? " underline underline-offset-2" : ""}`}>{value}</span>
                  </>
                );
                return (
                  <li key={value}>
                    {href ? (
                      <a href={href} {...(href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})} className="flex items-start gap-3 transition-colors hover:text-brand-600">{body}</a>
                    ) : (
                      <div className="flex items-start gap-3">{body}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* bottom bar */}
        <div className="flex flex-col items-center justify-between gap-3 border-t border-line py-6 text-xs text-ink-muted sm:flex-row">
          <p className="text-center sm:text-left">© {new Date().getFullYear()}{" "}
            <FooterHomeLink className="font-semibold text-brand-700 underline underline-offset-2 transition-colors hover:text-brand-600">{site.name}</FooterHomeLink>. All rights reserved. Powered by <a href="https://www.ammrs.in/" target="_blank" rel="noopener noreferrer" className="font-semibold text-brand-700 underline underline-offset-2 hover:text-brand-600">AMMRS</a></p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <Link href="/terms" className="hover:text-brand-600">Terms</Link>
            <Link href="/privacy" className="hover:text-brand-600">Privacy Policy</Link>
          </div>
        </div>
      </Container>
    </footer>
  );
}

function Col({ title, links }: { title: string; links: { label: string; href: string }[] }) {
  return (
    <div>
      <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-gold-600">{title}</h3>
      <ul className="mt-5 space-y-3 text-sm">
        {links.map((l) =>
          l.href === "/courses" ? (
            <li key={l.label} className="pt-1">
              <Link
                href={l.href}
                className="inline-flex items-center gap-1.5 rounded-full border border-brand-600/30 bg-brand-600/5 px-4 py-2 text-sm font-semibold text-brand-700 transition-colors hover:bg-brand-600 hover:text-white"
              >
                {l.label} <ArrowRight className="h-4 w-4" />
              </Link>
            </li>
          ) : (
            <li key={l.label}>
              <Link href={l.href} className="inline-block transition-all hover:translate-x-1 hover:text-brand-600">
                {l.label}
              </Link>
            </li>
          ),
        )}
      </ul>
    </div>
  );
}
